import React from 'react';
import { Card, Table, Pagination, Button, Space, DatePicker, Typography, Row, Col, Divider, Select, Tag } from 'antd';
import { DownloadOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useCourseReport from '../Hooks/Reports/useCourseReport';
import { CourseReportSortBy } from 'Common/Constants/Enums/Individual/Reports/CourseSortEnum';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

export default function CourseReport() {
  const {
    data,
    loading,
    pageNumber,
    pageSize,
    totalCount,
    filters,
    setPageNumber,
    setPageSize,
    setFilters,
    fetchCourseReport,
    downloadReport
  } = useCourseReport();

   const handleDownload = () => {
      const params = {
        SortBy: filters.SortBy,
      }
  
      if (filters.FromDate) {
        params.FromDate = dayjs(filters.FromDate).format('YYYY-MM-DD')
      }
      if (filters.ToDate) {
        params.ToDate = dayjs(filters.ToDate).endOf('day').format('YYYY-MM-DD')
      }
      if (filters.IsPaid !== undefined) {
        params.IsPaid = filters.IsPaid
      }
  
      downloadReport(params)
    }

  const columns = [
    {
      title: 'Course',
      render: (_, r) => (
        <>
          <Text strong>{r.title}</Text><br />
          <Text type="secondary">{r.description?.slice(0, 60)}...</Text>
        </>
      )
    },
    {
      title: 'Paid',
      dataIndex: 'isPaid',
      render: v => <Tag color={v ? 'green' : 'blue'}>{v ? 'Paid' : 'Free'}</Tag>
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: v => (v ? `$${v}` : '—')
    },
    {
      title: 'Published',
      dataIndex: 'isPublished',
      render: v => (v ? 'Yes' : 'No')
    },
    {
      title: 'Duration',
      dataIndex: 'estimatedTime'
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      render: d => dayjs(d).format('DD MMM YYYY')
    }
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Course Report</Title>
          <Text type="secondary">Course-wise analytics with filters and export</Text>
        </div>
        <Space>
          <Button onClick={() => window.history.back()}>Back</Button>
         <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload} disabled={loading}>
  Download
</Button>
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ marginTop: 16 }} title={<Space><FilterOutlined />Filters</Space>}>
        <Row gutter={16}>
          {/* Date Range */}
          <Col md={8}>
            <Text strong>Course Creation Period</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 6 }}
              onChange={(dates) => setFilters({
                ...filters,
                FromDate: dates?.[0]?.toDate(),
                ToDate: dates?.[1]?.toDate()
              })}
            />
          </Col>

          {/* Published */}
          <Col md={6}>
            <Text strong>Published</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={filters.IsPublished}
              placeholder="All"
              onChange={(value) => setFilters({ ...filters, IsPublished: value })}
            
            >
              <Select.Option value={undefined}>All</Select.Option>
              <Select.Option value={true}>Published</Select.Option>
              <Select.Option value={false}>Draft</Select.Option>
            </Select>
          </Col>

          {/* Paid */}
          <Col md={6}>
            <Text strong>Paid</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={filters.IsPaid}
              placeholder="All"
              onChange={(value) => setFilters({ ...filters, IsPaid: value })}
        
            >
              <Select.Option value={undefined}>All</Select.Option>
              <Select.Option value={true}>Paid</Select.Option>
              <Select.Option value={false}>Free</Select.Option>
            </Select>
          </Col>

          {/* Sort By */}
          <Col md={4}>
            <Text strong>Sort By</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={filters.SortBy}
              onChange={(v) => setFilters({ ...filters, SortBy: v })}
            >
              {Object.entries(CourseReportSortBy).map(([key, value]) => (
                <Select.Option key={value} value={value}>{key}</Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilters({
                FromDate: undefined,
                ToDate: undefined,
                IsPublished: undefined,
                IsPaid: undefined,
                SortBy: CourseReportSortBy.Latest
              });
              setPageNumber(1);
            }}
            disabled={loading}
          >
            Reset
          </Button>
          <Button type="primary" onClick={fetchCourseReport} disabled={loading}>Apply</Button>
        </Space>
      </Card>

      {/* Table */}
      <Card style={{ marginTop: 16 }}>
        <Table rowKey="id" columns={columns} dataSource={data} loading={loading} pagination={false} scroll={{ x: "max-content" }} />
        <Divider />
        <Pagination
          current={pageNumber}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          onChange={(p, s) => { setPageNumber(p); setPageSize(s); }}
        />
      </Card>
    </div>
  );
}
